import type { Lead, LeadStatus } from '@up2stay/up2promo-api-client';
import { Card, CardContent, CardHeader, CardTitle } from '@up2stay/up2promo-ui';
import { CopyButton } from '@up2stay/up2promo-ui';
import { Input } from '@up2stay/up2promo-ui';
import { PhoneInput } from '@up2stay/up2promo-ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@up2stay/up2promo-ui';
import { Label, Switch } from '@up2stay/up2promo-ui';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@up2stay/up2promo-ui';
import { cn } from '@up2stay/up2promo-ui';
import {
  Briefcase,
  Building2,
  CircleDot,
  type LucideIcon,
  Mail,
  MapPin,
  Phone,
  Share2,
  User,
  UserCircle2,
} from 'lucide-react';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import de from 'react-phone-number-input/locale/de';
import en from 'react-phone-number-input/locale/en';

import {
  LeadAssigneePicker,
  type LeadAssigneePickerLabels,
} from '@/core/components/leads/lead-assignee-picker';
import { LeadDetailEditHint } from '@/core/components/leads/lead-detail/lead-detail-edit-hint';
import {
  LEAD_DETAIL_EDITABLE_SURFACE_CLASS,
  LEAD_DETAIL_FLAT_CONTROL_CLASS,
  LEAD_DETAIL_PROPERTY_CARD_CONTENT_CLASS,
  LEAD_DETAIL_PROPERTY_CARD_HEADER_CLASS,
  LEAD_DETAIL_PROPERTY_CARD_TITLE_CLASS,
} from '@/core/components/leads/lead-detail/lead-detail-field-styles';
import {
  LEAD_STATUS_IDS,
  isLeadStatus,
} from '@/core/components/leads/lead-status-colors';
import { triggerSelectionHaptic } from '@/core/lib/dnd-haptics';

const PROPERTY_ACTION_BUTTON_CLASS =
  'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-foreground transition-colors hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50 [&_svg]:size-4';

const INLINE_FIELD_HEIGHT_CLASS = 'h-10 min-h-10';

const EDITABLE_FIELD_SURFACE_CLASS = cn(
  LEAD_DETAIL_EDITABLE_SURFACE_CLASS,
  INLINE_FIELD_HEIGHT_CLASS,
);

const PHONE_EDIT_ENTER_CLASS = 'animate-in fade-in-0 duration-200 ease-out';

const INLINE_PROPERTY_INPUT_CLASS = cn(
  INLINE_FIELD_HEIGHT_CLASS,
  LEAD_DETAIL_FLAT_CONTROL_CLASS,
  'w-full min-w-0 flex-1 cursor-text px-0 py-0 text-sm text-foreground placeholder:text-muted-foreground/60',
  // A long email ends in "…" while not focused instead of a hard cut (DEV-560).
  'text-ellipsis',
);

const INLINE_STATUS_TRIGGER_CLASS = cn(
  INLINE_FIELD_HEIGHT_CLASS,
  'flex w-full max-w-full cursor-pointer items-center py-0 text-sm font-normal leading-5 text-foreground [&>svg]:size-4 [&>svg]:opacity-60',
);

const EMPTY_PROPERTY_VALUE_CLASS = 'text-muted-foreground/70';

const INLINE_PHONE_INPUT_CLASS = cn(
  INLINE_FIELD_HEIGHT_CLASS,
  LEAD_DETAIL_FLAT_CONTROL_CLASS,
  'm-0 w-full min-w-0 p-0 focus-within:border-0 focus-within:ring-0 focus-within:ring-offset-0',
  '[&>div]:!m-0 [&>div]:!h-10 [&>div]:!min-h-10 [&>div]:!w-full',
  '[&_button]:!h-10 [&_button]:!min-h-10 [&_button]:rounded-s-md [&_button]:bg-transparent [&_button]:px-2 dark:[&_button]:bg-transparent',
  '[&_input]:!h-10 [&_input]:!min-h-10 [&_input]:border-0 [&_input]:bg-transparent [&_input]:px-0 [&_input]:py-0 [&_input]:text-sm [&_input]:shadow-none dark:[&_input]:bg-transparent',
);

export type ContactFormField =
  | 'email'
  | 'phone'
  | 'companyName'
  | 'jobTitle'
  | 'address';

export interface ContactForm {
  email: string;
  phone: string;
  companyName: string;
  jobTitle: string;
  address: string;
}

export interface LeadCrmProps {
  title: string;
  locale: string;
  /** Lead record owner (`lead.userId`); assignee defaults to this user on the server. */
  leadOwnerUserId: string;
  workspaceId: string;
  /** Workspace mandates sharing — omit per-lead share row entirely. */
  leadsSharingLockedByWorkspace: boolean;
  creatorLabel: string;
  creatorValue: string;
  assigneeLabel: string;
  assigneePlaceholder: string;
  assigneeTriggerLabel: string;
  /** Owner row label for assignee picker (stable; not from paginated search). */
  recordOwnerAssigneeLabel: string;
  assigneeDisplayReadonly: string;
  assigneeSearchPlaceholder: string;
  assigneeLookupEmpty: string;
  assigneePickerLabels: LeadAssigneePickerLabels;
  shareLabel: string;
  shareReadOnlyHint: string;
  sharedOnLabel: string;
  sharedOffLabel: string;
  assigneeUserId: string | null | undefined;
  /** True when lead.visibility === workspace */
  visibilityIsWorkspace: boolean;
  /** True when lead.visibility === private (share confirm before assign). */
  isPrivate: boolean;
  editable: boolean;
  isSavingShare: boolean;
  onAssigneeChange?: (userId: string | null, selectedLabel: string) => void;
  onShareChange?: (shared: boolean) => void;
}

interface LeadDetailPropertiesProps {
  lead: Lead;
  locale: string;
  layout?: 'sidebar' | 'compact' | 'contact';
  crm?: LeadCrmProps;
  labels: {
    properties: string;
    status: string;
    email: string;
    phone: string;
    company: string;
    jobTitle: string;
    address: string;
    /** "{field} kopieren" — name of the copy buttons (DEV-540). */
    copyField: string;
  };
  statusLabels: Record<string, string>;
  contactForm: ContactForm;
  isSavingContact: boolean;
  onContactFormChange: (form: ContactForm) => void;
  onContactFieldBlur: (field: ContactFormField) => void;
  /** Message under the email field when the value was rejected (DEV-571). */
  contactEmailError?: string | null;
  onStatusChange: (status: LeadStatus) => void;
  onCopy: () => void;
  /** When false, omit status row (hero already shows it). Default true for legacy layouts. */
  showStatus?: boolean;
}

/** Single-line value aligned like Select triggers (`leading-5` + `font-normal`). */
const READONLY_INLINE_VALUE_CLASS = cn(
  'block min-w-0 flex-1 truncate text-sm font-normal leading-5 text-foreground',
);

function CrmFields({ crm, compact }: { crm: LeadCrmProps; compact: boolean }) {
  const shareWorkspaceSwitchId = useId();
  const workspaceId = crm.workspaceId?.trim() ?? '';

  return (
    <>
      <PropertyRow icon={User} label={crm.creatorLabel} compact={compact}>
        <EditableFieldSurface inactive className="w-full">
          <span
            className={cn(
              READONLY_INLINE_VALUE_CLASS,
              !crm.creatorValue.trim() && EMPTY_PROPERTY_VALUE_CLASS,
            )}
          >
            {crm.creatorValue.trim() || '—'}
          </span>
        </EditableFieldSurface>
      </PropertyRow>

      <PropertyRow
        icon={UserCircle2}
        label={crm.assigneeLabel}
        compact={compact}
      >
        <EditableFieldSurface inactive={!crm.editable} className="w-full">
          <LeadAssigneePicker
            workspaceId={workspaceId}
            ownerUserId={crm.leadOwnerUserId}
            ownerOptionLabel={crm.recordOwnerAssigneeLabel}
            assigneeUserId={crm.assigneeUserId ?? undefined}
            triggerLabel={crm.assigneeTriggerLabel}
            triggerClassName={INLINE_STATUS_TRIGGER_CLASS}
            disabled={!workspaceId}
            editable={crm.editable}
            placeholder={crm.assigneePlaceholder}
            labels={crm.assigneePickerLabels}
            locale={crm.locale}
            isPrivate={crm.isPrivate}
            workspaceLeadsAlwaysShared={crm.leadsSharingLockedByWorkspace}
            showSavedCue
            onAssigneeChange={(next, label) =>
              crm.onAssigneeChange?.(next, label)
            }
          />
        </EditableFieldSurface>
      </PropertyRow>

      {workspaceId && !crm.leadsSharingLockedByWorkspace ? (
        <div
          className={cn(
            'flex min-h-10 items-center justify-between gap-4 border-b border-border/60 last:border-b-0',
            compact ? 'py-2' : 'py-2.5',
          )}
        >
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <Share2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <Label
              htmlFor={shareWorkspaceSwitchId}
              className={cn(
                'whitespace-nowrap text-xs font-medium leading-snug text-muted-foreground',
                !crm.editable ? '' : 'cursor-pointer',
              )}
            >
              {crm.shareLabel}
            </Label>
          </div>
          {crm.editable ? (
            <Switch
              id={shareWorkspaceSwitchId}
              checked={crm.visibilityIsWorkspace}
              disabled={crm.isSavingShare}
              onCheckedChange={(checked) => crm.onShareChange?.(checked)}
            />
          ) : (
            <TooltipProvider delayDuration={400}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex shrink-0">
                    <Switch
                      id={shareWorkspaceSwitchId}
                      checked={crm.visibilityIsWorkspace}
                      disabled
                    />
                  </span>
                </TooltipTrigger>
                <TooltipContent>{crm.shareReadOnlyHint}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      ) : null}
    </>
  );
}

function PropertyActionLink({
  href,
  label,
  icon: Icon,
  className,
}: {
  href: string;
  label: string;
  icon: LucideIcon;
  className?: string;
}) {
  return (
    <a
      href={href}
      className={cn(PROPERTY_ACTION_BUTTON_CLASS, className)}
      aria-label={label}
    >
      <Icon />
    </a>
  );
}

function EditableFieldSurface({
  children,
  className,
  /** Same outer sizing as interactive fields; no hover/focus chrome (e.g. read-only CRM lines). */
  inactive = false,
}: {
  children: ReactNode;
  className?: string;
  inactive?: boolean;
}) {
  return (
    <div
      className={cn(
        inactive
          ? cn(
              'flex min-w-0 items-center -mx-1.5 rounded-md px-1.5 py-0',
              INLINE_FIELD_HEIGHT_CLASS,
            )
          : EDITABLE_FIELD_SURFACE_CLASS,
        className,
      )}
    >
      {children}
    </div>
  );
}

function PropertyRow({
  icon: Icon,
  label,
  children,
  compact = false,
  /** Pencil sits beside the label for free-text contact fields. */
  showEditHint = false,
  /**
   * Desktop Kontakt / design row: icon · label · value · actions on one line.
   * Uses `minmax(0,1fr)` so long emails/phones truncate instead of overflowing.
   */
  inline = false,
}: {
  icon: typeof Mail;
  label: string;
  children: ReactNode;
  compact?: boolean;
  showEditHint?: boolean;
  inline?: boolean;
}) {
  if (inline) {
    return (
      <div className="grid min-h-[38px] min-w-0 grid-cols-[22px_minmax(0,7rem)_minmax(0,1fr)] items-center gap-2 border-t border-[color:var(--surface-border)] py-1 first:border-t-0">
        <Icon
          className="size-[15px] shrink-0 text-muted-foreground"
          aria-hidden
        />
        <span className="flex min-w-0 items-center gap-1 text-[12.5px] text-muted-foreground">
          <span className="truncate">{label}</span>
          {showEditHint ? (
            <LeadDetailEditHint className="size-3 shrink-0" />
          ) : null}
        </span>
        <div className="min-w-0 overflow-hidden">{children}</div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'min-w-0 border-b border-border/60 last:border-b-0',
        compact ? 'py-2' : 'py-2.5',
      )}
    >
      <div className="mb-1 flex items-center gap-2">
        <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="text-xs font-medium leading-none text-muted-foreground">
          {label}
        </span>
        {showEditHint ? <LeadDetailEditHint className="size-3" /> : null}
      </div>
      <div className="min-w-0 overflow-hidden pl-5">{children}</div>
    </div>
  );
}

function StatusSelect({
  lead,
  statusLabels,
  onStatusChange,
  triggerClassName,
}: {
  lead: Lead;
  statusLabels: Record<string, string>;
  onStatusChange: (status: LeadStatus) => void;
  triggerClassName?: string;
}) {
  return (
    <Select
      value={lead.status}
      onValueChange={(value) => {
        if (value == null || !isLeadStatus(value)) return;
        void triggerSelectionHaptic();
        onStatusChange(value);
      }}
    >
      <SelectTrigger unstyled className={triggerClassName}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {LEAD_STATUS_IDS.map((status) => (
          <SelectItem key={status} value={status}>
            {statusLabels[status] ?? status}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function ContactFieldActions({
  value,
  label,
  copyLabel,
  icon: Icon,
  hrefPrefix,
  onCopy,
}: {
  value: string;
  label: string;
  copyLabel: string;
  icon: LucideIcon;
  hrefPrefix: 'mailto' | 'tel';
  onCopy: () => void;
}) {
  return (
    <div className="flex shrink-0 items-center" data-lead-property-actions="">
      <CopyButton
        content={value}
        aria-label={copyLabel}
        variant="ghost"
        size="default"
        className="h-7 w-7"
        onCopy={onCopy}
      />
      <PropertyActionLink
        href={`${hrefPrefix}:${value}`}
        label={label}
        icon={Icon}
      />
    </div>
  );
}

function PropertiesForm({
  lead,
  locale,
  labels,
  statusLabels,
  contactForm,
  isSavingContact,
  onContactFormChange,
  onContactFieldBlur,
  contactEmailError,
  onStatusChange,
  onCopy,
  compact = false,
  showStatus = true,
  inline = false,
}: {
  lead: Lead;
  locale: string;
  labels: LeadDetailPropertiesProps['labels'];
  statusLabels: Record<string, string>;
  contactForm: ContactForm;
  isSavingContact: boolean;
  onContactFormChange: (form: ContactForm) => void;
  onContactFieldBlur: (field: ContactFormField) => void;
  /** Message under the email field when the value was rejected (DEV-571). */
  contactEmailError?: string | null;
  onStatusChange: (status: LeadStatus) => void;
  onCopy: () => void;
  compact?: boolean;
  showStatus?: boolean;
  /** Single-line Kontakt rows (desktop sidebar). */
  inline?: boolean;
}) {
  const phoneLabels = locale === 'de' ? de : en;
  const copyLabel = (field: string) =>
    labels.copyField.replace('{field}', field);
  const phoneEditRef = useRef<HTMLDivElement>(null);
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const phoneResetKey = `${lead.id}:${lead.phone ?? ''}`;
  const [prevPhoneResetKey, setPrevPhoneResetKey] = useState(phoneResetKey);

  if (phoneResetKey !== prevPhoneResetKey) {
    setPrevPhoneResetKey(phoneResetKey);
    setIsEditingPhone(false);
  }

  const commitPhoneEdit = useCallback(() => {
    setIsEditingPhone((editing) => {
      if (!editing) return editing;
      onContactFieldBlur('phone');
      return false;
    });
  }, [onContactFieldBlur]);

  useEffect(() => {
    if (!isEditingPhone) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (phoneEditRef.current?.contains(target)) return;
      if (
        target instanceof Element &&
        target.closest('[data-phone-country-overlay]')
      ) {
        return;
      }
      if (
        target instanceof Element &&
        target.closest('[data-lead-property-actions]')
      ) {
        return;
      }
      commitPhoneEdit();
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    return () =>
      document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [isEditingPhone, commitPhoneEdit]);

  return (
    <>
      {showStatus ? (
        <PropertyRow
          icon={CircleDot}
          label={labels.status}
          compact={compact}
          inline={inline}
        >
          <EditableFieldSurface className="w-full min-w-0">
            <StatusSelect
              lead={lead}
              statusLabels={statusLabels}
              onStatusChange={onStatusChange}
              triggerClassName={INLINE_STATUS_TRIGGER_CLASS}
            />
          </EditableFieldSurface>
        </PropertyRow>
      ) : null}

      <PropertyRow
        icon={Mail}
        label={labels.email}
        compact={compact}
        showEditHint
        inline={inline}
      >
        <EditableFieldSurface className="flex min-w-0 items-center gap-1">
          <Input
            type="email"
            inputMode="email"
            value={contactForm.email}
            onChange={(e) =>
              onContactFormChange({ ...contactForm, email: e.target.value })
            }
            onBlur={() => onContactFieldBlur('email')}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
            }}
            disabled={isSavingContact}
            placeholder="—"
            aria-label={labels.email}
            title={contactForm.email || undefined}
            className={INLINE_PROPERTY_INPUT_CLASS}
            aria-invalid={Boolean(contactEmailError)}
            aria-describedby={
              contactEmailError ? 'lead-contact-email-error' : undefined
            }
          />
          {contactForm.email.trim() && !contactEmailError ? (
            <ContactFieldActions
              value={contactForm.email.trim()}
              label={labels.email}
              copyLabel={copyLabel(labels.email)}
              icon={Mail}
              hrefPrefix="mailto"
              onCopy={onCopy}
            />
          ) : null}
        </EditableFieldSurface>
        {contactEmailError ? (
          <p
            id="lead-contact-email-error"
            className="mt-1 text-xs text-destructive"
            role="alert"
          >
            {contactEmailError}
          </p>
        ) : null}
      </PropertyRow>

      <PropertyRow
        icon={Phone}
        label={labels.phone}
        compact={compact}
        showEditHint
        inline={inline}
      >
        <EditableFieldSurface className="flex min-w-0 items-center gap-1">
          {isEditingPhone ? (
            <div
              ref={phoneEditRef}
              className={cn(
                'min-w-0 flex-1 overflow-hidden',
                PHONE_EDIT_ENTER_CLASS,
              )}
            >
              <PhoneInput
                defaultCountry="DE"
                labels={phoneLabels}
                value={contactForm.phone || undefined}
                onChange={(v) =>
                  onContactFormChange({ ...contactForm, phone: v || '' })
                }
                onBlur={commitPhoneEdit}
                disabled={isSavingContact}
                autoFocus
                className={INLINE_PHONE_INPUT_CLASS}
              />
            </div>
          ) : (
            <button
              type="button"
              className={cn(
                INLINE_PROPERTY_INPUT_CLASS,
                'cursor-pointer truncate text-left',
                !contactForm.phone.trim() && EMPTY_PROPERTY_VALUE_CLASS,
              )}
              onClick={() => setIsEditingPhone(true)}
              disabled={isSavingContact}
            >
              {contactForm.phone.trim() || '—'}
            </button>
          )}
          {contactForm.phone.trim() ? (
            <ContactFieldActions
              value={contactForm.phone.trim()}
              label={labels.phone}
              copyLabel={copyLabel(labels.phone)}
              icon={Phone}
              hrefPrefix="tel"
              onCopy={onCopy}
            />
          ) : null}
        </EditableFieldSurface>
      </PropertyRow>

      <PropertyRow
        icon={Building2}
        label={labels.company}
        compact={compact}
        showEditHint
        inline={inline}
      >
        <EditableFieldSurface className="flex min-w-0 items-center gap-1">
          <Input
            value={contactForm.companyName}
            onChange={(e) =>
              onContactFormChange({
                ...contactForm,
                companyName: e.target.value,
              })
            }
            onBlur={() => onContactFieldBlur('companyName')}
            disabled={isSavingContact}
            placeholder="—"
            aria-label={labels.company}
            className={INLINE_PROPERTY_INPUT_CLASS}
          />
          {contactForm.companyName.trim() ? (
            <CopyButton
              content={contactForm.companyName.trim()}
              aria-label={copyLabel(labels.company)}
              variant="ghost"
              size="default"
              className="h-7 w-7 shrink-0"
              onCopy={onCopy}
            />
          ) : null}
        </EditableFieldSurface>
      </PropertyRow>

      <PropertyRow
        icon={Briefcase}
        label={labels.jobTitle}
        compact={compact}
        showEditHint
        inline={inline}
      >
        <EditableFieldSurface className="flex min-w-0 items-center gap-1">
          <Input
            value={contactForm.jobTitle}
            onChange={(e) =>
              onContactFormChange({
                ...contactForm,
                jobTitle: e.target.value,
              })
            }
            onBlur={() => onContactFieldBlur('jobTitle')}
            disabled={isSavingContact}
            placeholder="—"
            aria-label={labels.jobTitle}
            className={INLINE_PROPERTY_INPUT_CLASS}
          />
          {contactForm.jobTitle.trim() ? (
            <CopyButton
              content={contactForm.jobTitle.trim()}
              aria-label={copyLabel(labels.jobTitle)}
              variant="ghost"
              size="default"
              className="h-7 w-7 shrink-0"
              onCopy={onCopy}
            />
          ) : null}
        </EditableFieldSurface>
      </PropertyRow>

      <PropertyRow
        icon={MapPin}
        label={labels.address}
        compact={compact}
        showEditHint
        inline={inline}
      >
        <EditableFieldSurface className="flex min-w-0 items-center gap-1">
          <Input
            value={contactForm.address}
            onChange={(e) =>
              onContactFormChange({
                ...contactForm,
                address: e.target.value,
              })
            }
            onBlur={() => onContactFieldBlur('address')}
            disabled={isSavingContact}
            placeholder="—"
            aria-label={labels.address}
            autoComplete="street-address"
            className={INLINE_PROPERTY_INPUT_CLASS}
          />
          {contactForm.address.trim() ? (
            <CopyButton
              content={contactForm.address.trim()}
              aria-label={copyLabel(labels.address)}
              variant="ghost"
              size="default"
              className="h-7 w-7 shrink-0"
              onCopy={onCopy}
            />
          ) : null}
        </EditableFieldSurface>
      </PropertyRow>
    </>
  );
}

function CompactProperties(
  props: LeadDetailPropertiesProps & { showStatus?: boolean },
) {
  const { crm, labels, showStatus = true } = props;
  return (
    <div className="flex w-full min-w-0 flex-col gap-4">
      <Card className="min-w-0 overflow-hidden">
        <CardHeader className={LEAD_DETAIL_PROPERTY_CARD_HEADER_CLASS}>
          <CardTitle className={LEAD_DETAIL_PROPERTY_CARD_TITLE_CLASS}>
            {labels.properties}
          </CardTitle>
        </CardHeader>
        <CardContent className={LEAD_DETAIL_PROPERTY_CARD_CONTENT_CLASS}>
          <PropertiesForm {...props} compact showStatus={showStatus} />
        </CardContent>
      </Card>
      {crm ? (
        <Card className="min-w-0 overflow-hidden">
          <CardHeader className={LEAD_DETAIL_PROPERTY_CARD_HEADER_CLASS}>
            <CardTitle className={LEAD_DETAIL_PROPERTY_CARD_TITLE_CLASS}>
              {crm.title}
            </CardTitle>
          </CardHeader>
          <CardContent className={LEAD_DETAIL_PROPERTY_CARD_CONTENT_CLASS}>
            <CrmFields crm={crm} compact />
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

function SidebarProperties(
  props: LeadDetailPropertiesProps & { showStatus?: boolean },
) {
  const { labels, crm, showStatus = true } = props;

  return (
    <aside className="flex min-w-0 w-full flex-col gap-4 md:gap-5">
      <Card className="min-w-0 overflow-hidden">
        <CardHeader className={LEAD_DETAIL_PROPERTY_CARD_HEADER_CLASS}>
          <CardTitle className={LEAD_DETAIL_PROPERTY_CARD_TITLE_CLASS}>
            {labels.properties}
          </CardTitle>
        </CardHeader>
        <CardContent className={LEAD_DETAIL_PROPERTY_CARD_CONTENT_CLASS}>
          <PropertiesForm {...props} showStatus={showStatus} />
        </CardContent>
      </Card>
      {crm ? (
        <Card className="min-w-0 overflow-hidden">
          <CardHeader className={LEAD_DETAIL_PROPERTY_CARD_HEADER_CLASS}>
            <CardTitle className={LEAD_DETAIL_PROPERTY_CARD_TITLE_CLASS}>
              {crm.title}
            </CardTitle>
          </CardHeader>
          <CardContent className={LEAD_DETAIL_PROPERTY_CARD_CONTENT_CLASS}>
            <CrmFields crm={crm} compact={false} />
          </CardContent>
        </Card>
      ) : null}
    </aside>
  );
}

export function LeadDetailProperties({
  layout = 'sidebar',
  showStatus = layout !== 'contact',
  ...props
}: LeadDetailPropertiesProps) {
  if (layout === 'contact') {
    return (
      <PropertiesForm {...props} showStatus={false} compact={false} inline />
    );
  }
  if (layout === 'compact') {
    return <CompactProperties {...props} showStatus={showStatus} />;
  }
  return <SidebarProperties {...props} showStatus={showStatus} />;
}
